import { ChangeDetectionStrategy, Component, computed, input, linkedSignal } from '@angular/core';
import { IconComponentSizeType } from './../../../types';
import { IconsInitConfigType } from '../../../constants';
import { iconFontGlyphs } from '../../../constants/icons.generated';
import { glyphEmBox } from './glyph-em-box';

/**
 * An icon is a glyph of the fr8bot-icons font (`web-*` ones of fr8bot-landing-icons), painted in
 * its own colour where it has one. `npm run icons:build` makes both fonts from
 * libs/shared-assets/svg-source; an icon that cannot be a glyph is a
 * plain SVG in libs/shared-assets/icons, shown with `<img>`.
 */
@Component({
  selector: 'fr8bot-ui-icon',
  host: {
    '[style.width.px]': 'hostWidth',
    '[style.min-width.px]': 'hostMinWidth',
    '[style.height.px]': 'hostHeight',
  },
  templateUrl: './icon.component.html',
  styleUrl: './icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconUIComponent {
  readonly name = input.required<IconsInitConfigType | null>();
  readonly size = input<IconComponentSizeType>();
  readonly class = input<string>();

  // A null name keeps the last icon on screen.
  readonly iconName = linkedSignal<IconsInitConfigType | null, IconsInitConfigType | null>({
    source: this.name,
    computation: (name, previous) => name ?? previous?.value ?? null,
  });

  protected readonly glyph = computed(() => {
    const name = this.iconName();

    return name ? iconFontGlyphs[name] : undefined;
  });

  protected readonly emBox = computed(() => glyphEmBox(this.glyph()?.aspect));

  get hostWidth() {
    const size = this.size();

    if (typeof size === 'number') {
      return size;
    }

    return size?.width;
  }

  get hostMinWidth() {
    const size = this.size();

    if (typeof size === 'number') {
      return size;
    }

    return size?.width;
  }

  get hostHeight() {
    const size = this.size();

    if (typeof size === 'number') {
      return size;
    }

    return size?.height;
  }
}
